import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Box, Button, Alert, Typography } from "@mui/material";
import styled from "styled-components";
import { colors } from "../modules/constants/colors";
import {
  registerController,
  alertModalController,
} from "../modules/controllers";
import {
  RegisterStepper,
  AccountInfoStep,
  PersonalDetailsStep,
  SecurityStep,
} from "../components/Register";

const RegisterContainer = styled.div`
  min-height: 100vh;
  background: #FDFBF9;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        ellipse at 20% 10%,
        rgba(204, 78, 48, 0.1) 0%,
        transparent 55%
      ),
      radial-gradient(
        ellipse at 80% 90%,
        rgba(204, 78, 48, 0.06) 0%,
        transparent 55%
      );
    pointer-events: none;
  }
`;

const RegisterCard = styled(Box)`
  max-width: 480px;
  width: 100%;
  padding: 48px 40px;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(44, 24, 16, 0.1), 0 2px 8px rgba(204, 78, 48, 0.08);
  background: rgba(255, 255, 255, 0.98);
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 32px 24px;
    margin: 16px;
    border-radius: 16px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const LogoImage = styled.img`
  height: 90px;
  width: auto;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 600px) {
    height: 75px;
    margin-bottom: 16px;
  }
`;

const RegisterTitle = styled(Typography)`
  color: #2C1810;
  font-weight: 700;
  text-align: center;
  margin-bottom: 8px;
  font-family: "Montserrat", sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.8rem;
  }
`;

const RegisterSubtitle = styled(Typography)`
  color: #93817E;
  text-align: center;
  margin-bottom: 40px;
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  line-height: 1.5;

  @media (max-width: 600px) {
    margin-bottom: 32px;
    font-size: 0.9rem;
  }
`;

const FormContainer = styled.form`
  width: 100%;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  gap: 16px;
`;

const StyledButton = styled(Button)`
  flex: 1;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-transform: none;
  font-family: "Montserrat", sans-serif;
  box-shadow: 0 6px 20px rgba(204, 78, 48, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(204, 78, 48, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled(Button)`
  flex: 1;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-transform: none;
  font-family: "Montserrat", sans-serif;
  border: 2px solid ${colors.primary};
  color: ${colors.primary};
  background: transparent;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(204, 78, 48, 0.05);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(204, 78, 48, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Register = observer(() => {
  const navigate = useNavigate();

  // Suppress ResizeObserver errors
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener("error", handleResizeObserverError);

    return () => {
      window.removeEventListener("error", handleResizeObserverError);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    registerController.updateFormData(name, newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("Form submitted, current step:", registerController.activeStep);
    console.log("Total steps:", registerController.steps.length);

    if (registerController.activeStep < registerController.steps.length - 1) {
      console.log("Moving to next step");
      registerController.nextStep();
    } else {
      // Final step - submit the form
      console.log("Final step - attempting to validate and register");
      console.log("Form data:", registerController.formData);
      console.log("Accept terms:", registerController.formData.acceptTerms);
      
      const isValid = registerController.validateStep(registerController.activeStep);
      console.log("Step validation result:", isValid);
      
      if (isValid) {
        console.log("Validation passed, calling register function");
        await registerController.register(() => {
          alertModalController.showMessage(
            "🎉 Welcome to Culinary IQ! Your account has been created successfully. You can now sign in to start your personalized meal planning journey."
          );

          registerController.resetForm();

          setTimeout(() => {
            navigate("/");
          }, 2000);
        });
      } else {
        console.log("Validation failed, not submitting");
        console.log("Errors:", registerController.errors);
      }
    }
  };

  const getErrorSeverity = (message) => {
    if (message.toLowerCase().includes("success") || message.includes("🎉")) {
      return "success";
    }
    if (
      message.toLowerCase().includes("already exists") ||
      message.toLowerCase().includes("taken")
    ) {
      return "warning";
    }
    return "error";
  };

  const getEnhancedErrorMessage = (message) => {
    const errorMappings = {
      "User with this email already exists":
        "An account with this email already exists. Would you like to sign in instead?",
      "Username already taken":
        "This username is already taken. Please try a different one.",
      "Please fill all fields":
        "Please complete all required fields before proceeding.",
      "Passwords do not match":
        "The passwords you entered do not match. Please check and try again.",
      "You must be at least 18 years of age to sign up":
        "You must be at least 18 years old to create an account.",
      "Please enter a valid email address":
        "Please enter a valid email address format (e.g., user@example.com).",
      "Something went wrong. Please try again later.":
        "We're experiencing technical difficulties. Please try again in a few moments.",
    };

    return errorMappings[message] || message;
  };

  const renderStepContent = () => {
    switch (registerController.activeStep) {
      case 0:
        return (
          <AccountInfoStep
            formData={registerController.formData}
            errors={registerController.errors}
            onChange={handleChange}
          />
        );
      case 1:
        return (
          <PersonalDetailsStep
            formData={registerController.formData}
            errors={registerController.errors}
            onChange={handleChange}
          />
        );
      case 2:
        return (
          <SecurityStep
            formData={registerController.formData}
            errors={registerController.errors}
            onChange={handleChange}
            passwordStrength={registerController.passwordStrength}
            onShowTerms={() => registerController.setShowTermsModal(true)}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard elevation={0}>
        <LogoContainer>
          <LogoImage src="/logo_vertical.png" alt="Culinary IQ Logo" />
          <RegisterTitle variant="h2">Create Your Account</RegisterTitle>
          <RegisterSubtitle variant="subtitle1">
            Join the future of personalized meal planning
          </RegisterSubtitle>
        </LogoContainer>

        {registerController.getRegisterMessage && (
          <Alert
            severity={getErrorSeverity(registerController.getRegisterMessage)}
            sx={{
              mb: 3,
              borderRadius: 2,
              fontFamily: "Montserrat",
              "& .MuiAlert-message": {
                fontSize: "0.9rem",
              },
            }}
          >
            {getEnhancedErrorMessage(registerController.getRegisterMessage)}
          </Alert>
        )}

        <RegisterStepper
          activeStep={registerController.activeStep}
          steps={registerController.steps}
        />

        <FormContainer onSubmit={handleSubmit}>
          {renderStepContent()}

          <ButtonContainer>
            {registerController.activeStep > 0 && (
              <SecondaryButton
                type="button"
                onClick={registerController.previousStep}
              >
                Back
              </SecondaryButton>
            )}
            <StyledButton
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: colors.primary,
                "&:hover": { backgroundColor: "#B03D22" },
                flex: registerController.activeStep === 0 ? 1 : undefined,
              }}
            >
              {registerController.activeStep ===
              registerController.steps.length - 1
                ? "Create Account"
                : "Next"}
            </StyledButton>
          </ButtonContainer>
        </FormContainer>

      </RegisterCard>
    </RegisterContainer>
  );
});

export default Register;
